/**
 * Large inviteClients mutations hit HTTP/router timeouts when many rows are sent at once.
 * Smaller sequential batches keep each GraphQL request within a safe duration.
 */
export const INVITE_CLIENTS_BATCH_SIZE = 75

export function chunkArray<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items]
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

export type ErroredClientPayload = {
  email?: string | null
  firstName?: string | null
  first_name?: string | null
  lastName?: string | null
  last_name?: string | null
  phone?: string | null
  notes?: string | null
}

export type InviteClientsBatchResult = {
  data?: {
    inviteClients?: {
      erroredClients?: ErroredClientPayload[]
    }
  }
  error?: unknown
}

/** Fired before the first batch (all zeros) and after each successful batch. */
export type InviteImportProgress = {
  completedBatches: number
  batchCount: number
  processedRows: number
  totalRows: number
}

export function getInviteImportBatchCount(totalRows: number): number {
  if (totalRows <= 0) return 0
  return Math.ceil(totalRows / INVITE_CLIENTS_BATCH_SIZE)
}

export async function inviteClientsInBatches<T extends Record<string, unknown>>(
  clients: T[],
  inviteBatch: (vars: { clients: T[] }) => Promise<InviteClientsBatchResult>,
  onProgress?: (progress: InviteImportProgress) => void
): Promise<{ erroredClients: ErroredClientPayload[]; lastError?: unknown }> {
  if (clients.length === 0) {
    return { erroredClients: [] }
  }

  const batches = chunkArray(clients, INVITE_CLIENTS_BATCH_SIZE)
  const batchCount = batches.length
  const erroredClients: ErroredClientPayload[] = []
  let lastError: unknown
  let processedRows = 0

  onProgress?.({ completedBatches: 0, batchCount, processedRows: 0, totalRows: clients.length })

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i]!
    const result = await inviteBatch({ clients: batch })
    if (result.error) {
      lastError = result.error
      break
    }
    const batchErrors = result.data?.inviteClients?.erroredClients ?? []
    erroredClients.push(...batchErrors)
    processedRows += batch.length
    onProgress?.({ completedBatches: i + 1, batchCount, processedRows, totalRows: clients.length })
  }

  return { erroredClients, lastError }
}
